import { Injectable } from '@angular/core';
import { Assignment } from '../../api/app-types/assignment';
import { User } from '../../api/app-types/user.app';

@Injectable({
	providedIn: 'root',
})
export class AccessService {
	public canAccessAdditionalAssignments(assignments: Assignment[], user: User): boolean {
		return this.canAccessAdditionalAssignmentsForClient(assignments, user);
	}

	public canAccessAdditionalAssignmentsForClient(assignments: Assignment[], user: User, clientId?: number): boolean {
		const userIds = this.normalizeUserIds(user);
		const additionalAssignments = this.collectAdditionalAssignments(assignments);
		return additionalAssignments.some((assignment) => {
			if (clientId != null && assignment.clientId !== clientId) return false;
			const ownerIds = this.normalizeAssignmentOwnerIds(assignment);
			return ownerIds.some((id) => userIds.has(id));
		});
	}

	private collectAdditionalAssignments(assignments: Assignment[]): Assignment[] {
		const nested = assignments.flatMap((assignment) =>
			assignment.additionalAssignments?.length ? assignment.additionalAssignments : [],
		);
		return [...assignments.filter((assignment) => assignment.isAdditionalAssignment), ...nested];
	}

	public isAdditionalOwner(assignment: Assignment, user: User): boolean {
		const userIds = this.normalizeUserIds(user);
		const owner = assignment.ownerOfAdditionalAssignment;
		if (owner) {
			const ownerIds = [owner.id, owner.objectId].filter((id) => id != null).map(String);
			return ownerIds.some((id) => userIds.has(id));
		}
		if (assignment.createdByUser) {
			const createdByIds = [assignment.createdByUser.id, assignment.createdByUser.objectId]
				.filter((id) => id != null)
				.map(String);
			return createdByIds.some((id) => userIds.has(id));
		}
		return false;
	}

	public isMainOwner(assignment: Assignment, user: User): boolean {
		if (assignment.isAdditionalAssignment) return false;
		if (assignment.createdByUser) {
			const userIds = this.normalizeUserIds(user);
			const createdByIds = [assignment.createdByUser.id, assignment.createdByUser.objectId]
				.filter((id) => id != null)
				.map(String);
			return createdByIds.some((id) => userIds.has(id));
		}
		const additionalOwned = (assignment.additionalAssignments ?? []).filter((a) => this.isAdditionalOwner(a, user));
		return additionalOwned.length === 0;
	}

	private normalizeUserIds(user: User): Set<string> {
		return new Set([user.id, user.objectId].filter((id): id is string => !!id).map(String));
	}

	private normalizeAssignmentOwnerIds(assignment: Assignment): string[] {
		const createdBy = assignment.createdByUser?.id ? String(assignment.createdByUser.id) : '';
		const createdByObjectId = assignment.createdByUser?.objectId ? String(assignment.createdByUser.objectId) : '';
		const ownerId = assignment.ownerOfAdditionalAssignment?.id ? String(assignment.ownerOfAdditionalAssignment.id) : '';
		const ownerObjectId = assignment.ownerOfAdditionalAssignment?.objectId
			? String(assignment.ownerOfAdditionalAssignment.objectId)
			: '';
		return [createdBy, createdByObjectId, ownerId, ownerObjectId].filter((id) => id.length > 0);
	}
}
